import { api } from './api'

/**
 * #193 — client de la liste « À rappeler » (entrants manqués) et de sa mesure.
 * Contrat serveur : `apps/backend/src/missed-calls/` (lecture de `Call`, tout
 * PAR NUMÉRO ; #396 : le nom de la fiche du tenant quand le numéro en a une).
 */

export interface CallbackAgent {
  id: string
  name: string | null
}

/** Un numéro à rappeler — une ligne par numéro appelant. */
export interface CallbackItem {
  /** E.164 — toujours rappelable (les masqués sont comptés à part). */
  number: string
  /** Manqués en attente depuis le dernier traitement du numéro. */
  attempts: number
  firstMissedAt: string
  lastMissedAt: string
  /** Dernier manqué en attente. */
  lastCallId: string
  /** Rappels PARTIS restés sans réponse depuis le dernier manqué (0..2 — au 3ᵉ la ligne sort). */
  callbackTries: number
  /** Heure du dernier de ces rappels. */
  lastTryAt: string | null
  /** Dernier agent ayant appelé ce numéro (90 j) — règle #157. */
  lastAgent: CallbackAgent | null
  /** Vrai si je suis ce dernier agent. */
  mine: boolean
  /** Vrai si le numéro n'a aucun agent (visible de tous, « Non attribué »). */
  unassigned: boolean
  /**
   * PAUSE-PLATEAU : vrai si l'un des manqués en attente a été refusé parce
   * que le plateau était en pause (annonce « fermé », personne n'a sonné).
   * Optionnel : un backend antérieur au lot ne le renvoie pas.
   */
  missedWhilePaused?: boolean
  /**
   * #396 — fiche contact du tenant portant ce numéro (la préférée en cas de
   * doublons, #291) ; `null` sans fiche. Optionnel : un backend antérieur au
   * lot ne le renvoie pas.
   */
  contactId?: string | null
  /**
   * #396 — nom de cette fiche ; `null` sans fiche ou nom inutilisable
   * (« Inconnu »). Texte saisi par un TIERS : rendu par React (échappé),
   * jamais journalisé. Règle d'affichage : `callbackContactName`.
   */
  contactName?: string | null
}

/** Filtre de l'ADMIN. Ignoré par le serveur pour tout autre rôle. */
export type CallbackScope = 'all' | 'mine' | 'unassigned'

export interface CallbackAgentCount extends CallbackAgent {
  count: number
}

export interface CallbackList {
  items: CallbackItem[]
  /**
   * Portée appliquée par le SERVEUR d'après le rôle : `team` (ADMIN, tout le
   * plateau) ou `own` (mes numéros + les non attribués). L'écran s'y fie —
   * il ne déduit jamais la portée du rôle local.
   */
  viewer: 'team' | 'own'
  counts: {
    /** Numéros VISIBLES de moi (tout le plateau pour l'ADMIN). */
    total: number
    mine: number
    unassigned: number
    hidden: number
    /** ADMIN seulement. */
    byAgent: CallbackAgentCount[]
  }
  windowDays: number
}

export interface MissedStatsDay {
  date: string
  inbound: number
  missed: number
  /** Manqués dont le prospect a ensuite été joint. */
  reached: number
  /** … dont joints en ≤ 24 h. */
  recovered24h: number
  classified: number
  abandoned: number
  pending: number
  hidden: number
}

export interface MissedStats {
  days: MissedStatsDay[]
  totals: Omit<MissedStatsDay, 'date'>
  /** NUMÉROS à rappeler (7 × 24 h glissantes) — le chiffre du badge. */
  pendingNumbers: number
  /** Par agent (numéros des 7 × 24 h) ; `agent: null` = non attribués. Vide en vue tous tenants. */
  byAgent?: MissedStatsAgent[]
  timeZone: string
}

export interface MissedStatsAgent {
  agent: CallbackAgent | null
  pending: number
  reached: number
  abandoned: number
  classified: number
}

/** Issues admises pour classer un numéro sans le rappeler. */
export type CallbackDismissOutcome = 'NOT_INTERESTED' | 'WRONG_NUMBER'

const EMPTY_LIST: CallbackList = {
  items: [],
  viewer: 'own',
  counts: { total: 0, mine: 0, unassigned: 0, hidden: 0, byAgent: [] },
  windowDays: 7,
}

/**
 * Le badge vit dans l'AppShell : une réponse MAL FORMÉE (backend pas encore
 * déployé derrière un frontend neuf, proxy qui rend une page HTML en 200…) ne
 * doit jamais faire tomber tout l'écran. Tout ce qui n'a pas la forme du
 * contrat est lu comme « rien à rappeler ».
 */
export function normalizeCallbackList(data: unknown): CallbackList {
  const candidate = data as Partial<CallbackList> | null | undefined
  if (!candidate || typeof candidate !== 'object' || !Array.isArray(candidate.items)) {
    return EMPTY_LIST
  }
  const counts = candidate.counts
  const count = (value: unknown, fallback: number) =>
    typeof value === 'number' && Number.isFinite(value) ? value : fallback
  return {
    items: candidate.items,
    // Tout ce qui n'est pas explicitement `team` est lu comme la portée la
    // plus étroite : un doute ne doit jamais afficher les filtres du plateau.
    viewer: candidate.viewer === 'team' ? 'team' : 'own',
    counts: {
      total: count(counts?.total, candidate.items.length),
      mine: count(counts?.mine, 0),
      unassigned: count(counts?.unassigned, 0),
      hidden: count(counts?.hidden, 0),
      byAgent: Array.isArray(counts?.byAgent) ? counts.byAgent : [],
    },
    windowDays: count(candidate.windowDays, 7),
  }
}

export const missedCallsApi = {
  list: async (scope: CallbackScope = 'all'): Promise<CallbackList> => {
    const { data } = await api.get('/missed-calls', {
      params: scope === 'all' ? undefined : { scope },
    })
    return normalizeCallbackList(data)
  },

  /**
   * Classe un numéro (« ne pas rappeler ») : le serveur pose l'issue sur ses
   * manqués en attente ET trace l'auteur (qui, quand).
   */
  dismiss: async (
    number: string,
    outcome: CallbackDismissOutcome = 'NOT_INTERESTED',
  ): Promise<{ number: string; dismissed: number }> => {
    const { data } = await api.post('/missed-calls/dismiss', { number, outcome })
    return data
  },

  /** `null` si la réponse n'a pas la forme attendue (la carte se tait). */
  stats: async (days: number): Promise<MissedStats | null> => {
    const { data } = await api.get('/missed-calls/stats', {
      params: { days, tz: Intl.DateTimeFormat().resolvedOptions().timeZone },
    })
    return data && typeof data === 'object' && data.totals ? data : null
  },
}
